import { AdSlotType, type AdCampaign, type AdCreative } from './AdTypes';

/**
 * Ad manifest = the JSON a backend delivers (for now the static file public/ads/campaigns.json):
 *
 *   { "version": 1, "campaigns": [ { campaignId, companyId, companyName, startDate, endDate, weight,
 *       slotTypes: [...], maxImpressions?, creatives: [ { creativeId, textureUrl, slotTypes, label } ] } ] }
 *
 * Creatives are plain images (JPEG/PNG/WebP) in the format of their slot type (see AD_FORMATS).
 * Invalid entries are skipped with a readable error – one broken booking never blanks all ads.
 */
export const AD_MANIFEST_VERSION = 1;

const SLOT_TYPES = new Set<string>(Object.values(AdSlotType));

export interface AdManifestResult {
  readonly campaigns: AdCampaign[];
  readonly errors: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function slotTypes(value: unknown, where: string, errors: string[]): AdSlotType[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${where}: slotTypes must be a non-empty list`);
    return null;
  }
  const unknown = value.filter((v) => typeof v !== 'string' || !SLOT_TYPES.has(v));
  if (unknown.length > 0) {
    errors.push(`${where}: unknown slot types ${unknown.join(', ')}`);
    return null;
  }
  return value as AdSlotType[];
}

function parseCreative(raw: unknown, campaignId: string, index: number, errors: string[]): AdCreative | null {
  const where = `${campaignId}.creatives[${index}]`;
  if (!isRecord(raw) || !text(raw.creativeId) || !text(raw.textureUrl)) {
    errors.push(`${where}: creativeId and textureUrl are required`);
    return null;
  }
  const types = slotTypes(raw.slotTypes, where, errors);
  if (!types) return null;
  return {
    creativeId: raw.creativeId,
    campaignId,
    textureUrl: raw.textureUrl,
    slotTypes: types,
    label: text(raw.label) ? raw.label : raw.creativeId,
  };
}

function parseCampaign(raw: unknown, index: number, errors: string[]): AdCampaign | null {
  if (!isRecord(raw) || !text(raw.campaignId)) {
    errors.push(`campaigns[${index}]: campaignId is required`);
    return null;
  }
  const id = raw.campaignId;
  if (!text(raw.companyId) || !text(raw.companyName)) {
    errors.push(`${id}: companyId and companyName are required`);
    return null;
  }
  if (!text(raw.startDate) || !text(raw.endDate) || Number.isNaN(Date.parse(raw.startDate)) || Number.isNaN(Date.parse(raw.endDate))) {
    errors.push(`${id}: startDate/endDate must be ISO 8601 dates`);
    return null;
  }
  const types = slotTypes(raw.slotTypes, id, errors);
  if (!types) return null;
  const creatives = (Array.isArray(raw.creatives) ? raw.creatives : [])
    .map((c, i) => parseCreative(c, id, i, errors))
    .filter((c): c is AdCreative => c !== null);
  if (creatives.length === 0) {
    errors.push(`${id}: no valid creative`);
    return null;
  }
  const weight = typeof raw.weight === 'number' && raw.weight > 0 ? raw.weight : 1;
  const maxImpressions = typeof raw.maxImpressions === 'number' ? raw.maxImpressions : undefined;
  return {
    campaignId: id,
    companyId: raw.companyId,
    companyName: raw.companyName,
    startDate: raw.startDate,
    endDate: raw.endDate,
    weight,
    slotTypes: types,
    creatives,
    ...(maxImpressions === undefined ? {} : { maxImpressions }),
  };
}

export function parseAdManifest(json: unknown): AdManifestResult {
  const errors: string[] = [];
  if (!isRecord(json) || !Array.isArray(json.campaigns)) return { campaigns: [], errors: ['manifest: "campaigns" list missing'] };
  if (json.version !== AD_MANIFEST_VERSION) errors.push(`manifest: version ${String(json.version)} (expected ${AD_MANIFEST_VERSION})`);
  const campaigns = json.campaigns.map((c, i) => parseCampaign(c, i, errors)).filter((c): c is AdCampaign => c !== null);
  return { campaigns, errors };
}
